import { sharedReactorAuthentication as authentication } from '../authentication';
import { sessionService } from '../services/sessionservice.js';
import { meetingService } from '../services/meetingservice.js';
import { projectService } from '../services/projectservice.js';
import { DefaultTheme, addDefaultThemeProps, toLatestThemePropsVersion } from '../theme.js';
import log from '../log.js';

let Vue = null;

function vueSet(array, key, value) {
  if (Vue) {
    Vue.set(array, key, value);
  } else {
    array[key] = value;
  }
}

function vueDelete(array, key) {
  if (Vue) {
    Vue.delete(array, key);
  } else {
    delete array[key];
  }
}

export default {
  namespaced: true,
  state: {
    sessionList: {},
    projectInfoList: {},
    projectCustomizations: {},
    meetingConnectionCounts: {},
    meetingPublicAccessKeys: {},
    clientAccess: {},
    mainThemeProjectId: null,
    modifiedThemeProperties: null,
    meetingSettings: {},
    tenancyMeetingIdMappings: {},
    projectMeetingIdMappings: {},
    currentProjectId: null
  },
  init: function (vue, store) {
    Vue = vue;
  },
  mutations: {
    projectInfoAdded(state, info) {
      vueSet(state.projectInfoList, info.projectId, info);
    },
    projectInfoRemoved(state, project_id) {
      vueDelete(state.projectInfoList, project_id);
    },
    projectFlaggedAsDeleted(state, deleted_msg) {
      let item = state.projectInfoList[deleted_msg.projectId];
      if (item) {
        item.isDeleted = true;
        item.projectDeletedMsg = deleted_msg;
      }
    },
    projectFlaggedAsUnshared(state, msg) {
      let item = state.projectInfoList[msg.projectId];
      if (item) {
        item.unShared = true;
      }
    },
    sessionUpdated(state, session) {
      vueSet(state.sessionList, session.sessionId, session);
    },
    projectUpdated(state, project) {
      let item = state.projectInfoList[project.projectId];
      if (item) {
        item.project = project;
      }
    },
    setCurrentProjectId(state, project_id) {
      state.currentProjectId = parseInt(project_id);
    },
    projectCustomizationUpdated(state, customization) {
      vueSet(state.projectCustomizations, customization.projectId, customization);
    },
    modifiyThemeProperties(state, { project_id, modified_props, props_to_delete }) {
      var customization = state.projectCustomizations[project_id];
      var existing_props = customization != null ? customization.projectCustomizationTheme != null ? customization.projectCustomizationTheme.projectThemeProperties : null : null;
      if (existing_props == null) existing_props = {};
      var props_map = JSON.parse(JSON.stringify(existing_props));
      for (var key in modified_props) {
        props_map[key] = modified_props[key];
      }
      for (var i = 0; i < props_to_delete.length; i++) {
        delete props_map[props_to_delete[i]];
      }
      if (customization) {
        if (customization.projectCustomizationTheme == null) {
          customization.projectCustomizationTheme = {};
        }
        customization.projectCustomizationTheme.projectThemeProperties = props_map;
        vueSet(state.projectCustomizations, project_id, customization);
      }
    },
    meetingSettingsUpdated(state, update) {
      var settings = {};
      for (let item of update.settings) {
        settings[item.key] = item.setting;
      }
      vueSet(state.meetingSettings, update.projectId, settings);
    },
    meetingPublicAccessKeysInitialData(state, initial_data) {
      var key_map = {};
      for (let key of initial_data.keys) {
        if (!key_map[key.keyType]) {
          key_map[key.keyType] = [];
        }
        key_map[key.keyType].push(key);
      }
      vueSet(state.meetingPublicAccessKeys, initial_data.projectId, key_map);
    },
    meetingPublicAccessKeyUpdated(state, access_key) {
      var existing_keys = state.meetingPublicAccessKeys[access_key.projectId];
      if (existing_keys == null) existing_keys = {};
      var key_map = JSON.parse(JSON.stringify(existing_keys));

      if (!key_map[access_key.keyType]) {
        key_map[access_key.keyType] = [access_key];
      } else {
        var found = false;
        for (var i = 0; i < key_map[access_key.keyType].length; i++) {
          if (key_map[access_key.keyType][i].keyId == access_key.keyId) {
            key_map[access_key.keyType][i] = access_key;
            found = true;
            break;
          }
        }
        if (!found) {
          key_map[access_key.keyType].push(access_key);
        }
      }

      vueSet(state.meetingPublicAccessKeys, access_key.projectId, key_map);
    },
    meetingPublicAccessKeyDeleted(state, access_key) {
      var existing_keys = state.meetingPublicAccessKeys[access_key.projectId];
      if (existing_keys == null) existing_keys = {};
      var key_map = JSON.parse(JSON.stringify(existing_keys));

      if (key_map[access_key.keyType]) {
        var found = false;
        for (var i = 0; i < key_map[access_key.keyType].length; i++) {
          if (key_map[access_key.keyType][i].keyId == access_key.keyId) {
            key_map[access_key.keyType].splice(i, 1);
            break;
          }
        }
      }

      vueSet(state.meetingPublicAccessKeys, access_key.projectId, key_map);
    },
    clientAccessUpdated(state, client_access) {
      vueSet(state.clientAccess, client_access.projectId, client_access);
    },
    setModifiedThemeProperties(state, theme) {
      state.modifiedThemeProperties = theme;
    },
    setMainThemeProjectId(state, project_id) {
      state.mainThemeProjectId = project_id;
    },
    meetingConnectionCountUpdated(state, count) {
      vueSet(state.meetingConnectionCounts, count.projectId, count);
    },
    tenancyMeetingIdMappingsUpdated(state, update) {
      vueSet(state.tenancyMeetingIdMappings, update.projectId, update.externalMeetingIds);
    },
    projectMeetingIdMappingsUpdated(state, update) {
      vueSet(state.projectMeetingIdMappings, update.projectId, update.externalMeetingIds);
    }
  },
  getters: {
    getProjectBySessionId: (state, getters) => session_id => {
      let info = getters.getProjectInfoBySessionId(session_id);
      return info == null ? null : info.project;
    },
    getProjectInfoBySessionId: state => session_id => {
      let session = state.sessionList[session_id];
      return session == null ? null : state.projectInfoList[session.projectId];
    },
    getProjectById: state => project_id => {
      return state.projectInfoList[project_id];
    },
    getProjectId: state => session_id => {
      let session = state.sessionList[session_id];
      return session == null ? null : state.sessionList[session_id].projectId;
    },
    getAppId: (state, getters) => session_id => {
      let project = getters.getProjectBySessionId(session_id);
      return project == null ? null : project.appId;
    },
    getProjectCustomization: state => project_id => {
      return state.projectCustomizations[project_id];
    },
    getPublicMeetingAccessKeys: state => project_id => {
      return state.meetingPublicAccessKeys[project_id];
    },
    getClientAccess: state => project_id => {
      return state.clientAccess[project_id];
    },
    getProjectTheme: (state, getters) => project_id => {
      let customization = getters.getProjectCustomization(project_id);
      if (customization == null) return null;
      return customization.projectCustomizationTheme;
    },
    getProjectThemeProperties: (state, getters) => project_id => {
      let theme = getters.getProjectTheme(project_id);
      if (theme == null) return null;
      return theme.projectThemeProperties;
    },
    getCurrentThemeProps: (state, getters) => project_id => {
      if (state.modifiedThemeProperties != null) return state.modifiedThemeProperties;

      if (project_id == null) {
        project_id = state.mainThemeProjectId;
      }
      if (project_id == null) return DefaultTheme;

      let theme = getters['getProjectThemeProperties'](project_id);

      //log.debug('### theme = ' + (theme == null ? 'null' : JSON.stringify(theme)))
      //if (theme != null) log.debug('### toInternalThemeProps = ' + JSON.stringify(toInternalThemeProps(theme)))

      return theme != null ? addDefaultThemeProps(theme) : DefaultTheme;
    },
    getMeetingConnectionCount: state => project_id => {
      return state.meetingConnectionCounts[project_id];
    },
    getMeetingSettings: state => project_id => {
      return state.meetingSettings[project_id];
    },
    getMeetingSetting: state => (project_id, key) => {
      if (state.meetingSettings[project_id] == null) return null;
      return state.meetingSettings[project_id][key];
    },
    getSessionListForProject: state => project_id => {
      var session_ids = Object.keys(state.sessionList);
      var ret_list = [];
      for (let session_id of session_ids) {
        let session = state.sessionList[session_id];
        if (session.projectId == project_id) ret_list.push(session);
      }
      return ret_list;
    },
    getTenancyMeetingIdMappings: state => project_id => {
      if (state.tenancyMeetingIdMappings[project_id] == null) return [];
      return state.tenancyMeetingIdMappings[project_id];
    },
    getProjectMeetingIdMappings: state => project_id => {
      if (state.projectMeetingIdMappings[project_id] == null) return [];
      return state.projectMeetingIdMappings[project_id];
    },
    currentProjectId(state) {
      return state.currentProjectId;
    }
  },
  actions: {
    // parameters: tenancyId AND (sessionId or projectId)
    addProjectListener(context, { tenancyId, projectId, sessionId }) {
      // TODO: check if we already have session and project before making a request..
      let project_id_promise = projectId === undefined ? getSession(context, sessionId).then(s => s.projectId) : Promise.resolve(projectId);

      return project_id_promise.then(project_id => getProject(context, tenancyId, project_id)).then(project => {
        let project_id = project.projectId;
        let info = getProjectInfo(context, project_id);
        let create_info = info == null;

        // create new project wrapper object
        if (create_info) {
          info = {
            projectId: project_id,
            project: project,
            isDeleted: false,
            unShared: false,
            projectDeletedMsg: null,
            unsubscribe: null
          };
          projectListenerCounts[project_id] = 0;
        }

        // setup project subscription if we are not already subscribed
        if (projectListenerCounts[project_id] === 0) {
          // subscribe and add handlers
          let project_subscription = meetingService.subscribeToProject(project_id, response => {
            if (response.status !== 'success') {
              context.commit('errors/addError', response, { root: true });
            }
          }, {
            'ProjectUpdated': msg => {
              context.commit('projectUpdated', msg.project);
            },
            'ProjectDeleted': msg => {
              context.commit('projectFlaggedAsDeleted', msg);
            },
            'ProjectUnshared': msg => {
              context.commit('projectFlaggedAsUnshared', msg);
            },
            'MeetingConnectionCount': msg => {
              context.commit('meetingConnectionCountUpdated', msg);
            },
            'DashboardProjectCustomizationUpdated': msg => {
              if (msg.projectCustomization.projectCustomizationTheme != null && msg.projectCustomization.projectCustomizationTheme.projectThemeProperties != null) {
                // avoid "do not mutate vuex store state outside mutation handlers" error
                msg.projectCustomization = JSON.parse(JSON.stringify(msg.projectCustomization));
                msg.projectCustomization.projectCustomizationTheme.projectThemeProperties = toLatestThemePropsVersion(msg.projectCustomization.projectCustomizationTheme.projectThemeProperties);
              }

              context.commit('projectCustomizationUpdated', msg.projectCustomization);

              // clear modifiedThemeProperties if the customization properties are the same
              if (context.state.modifiedThemeProperties != null && msg.projectCustomization.projectCustomizationTheme != null && msg.projectCustomization.projectCustomizationTheme.projectThemeProperties != null) {
                let match = true;
                for (name in context.state.modifiedThemeProperties) {
                  if (name === 'audienceDisplayLogoDownloadUrl' || name === 'audienceDisplayLogoRes') continue;
                  if (name === 'clientLogoDownloadUrl' || name === 'clientLogoRes') continue;
                  if (context.state.modifiedThemeProperties[name] !== msg.projectCustomization.projectCustomizationTheme.projectThemeProperties[name]) {
                    match = true;
                    break;
                  }
                }
                if (match) {
                  context.commit('setModifiedThemeProperties', null);
                }
              }
            },
            'DashboardMeetingSettingsUpdated': msg => {
              context.commit('meetingSettingsUpdated', msg);
            },
            'PublicMeetingAccessKeyInitialData': msg => {
              context.commit('meetingPublicAccessKeysInitialData', msg);
            },
            'PublicMeetingAccessKey': msg => {
              context.commit('meetingPublicAccessKeyUpdated', msg);
            },
            'DeletedPublicMeetingAccessKey': msg => {
              context.commit('meetingPublicAccessKeyDeleted', msg);
            },
            'DashboardClientAccessUpdated': msg => {
              context.commit('clientAccessUpdated', msg);
            },
            'TenancyMeetingIdMappings': msg => {
              context.commit('tenancyMeetingIdMappingsUpdated', msg);
            },
            'ProjectMeetingIdMappings': msg => {
              context.commit('projectMeetingIdMappingsUpdated', msg);
            }
          });
          let session_list_subscription = null;
          if (projectId !== undefined) {
            session_list_subscription = sessionService.subscribeToSessionList(project_id, response => {
              if (response.status !== 'success') {
                context.commit('errors/addError', response, { root: true });
              }
            }, {
              'SessionList': response => {
                for (var i = 0; i < response.sessions.length; i++) {
                  context.commit('sessionUpdated', response.sessions[i]);
                }
              }
            });
          }

          // create unsubscribe function
          info.unsubscribe = function () {
            projectListenerCounts[project_id]--;
            if (projectListenerCounts[project_id] === 0) {
              project_subscription.unsubscribe();
              if (session_list_subscription) {
                session_list_subscription.unsubscribe();
              }
              context.commit('projectInfoRemoved', project_id);
            }
          };
        }

        // increment listener count
        projectListenerCounts[project_id]++;

        // add poll info to state
        if (create_info) {
          context.commit('projectInfoAdded', info);
        }

        return {
          projectId: project_id,
          remove: () => info.unsubscribe()
        };
      });
    },
    modifiyThemeProperties(context, { projectId, modifiedProps, propsToDelete }) {
      context.commit('modifiyThemeProperties', { project_id: projectId, modified_props: modifiedProps, props_to_delete: propsToDelete });
    },
    applyModifiedTheme(context, { projectId, tenancyId }) {
      meetingService.updateProjectCustomizationTheme(projectId, tenancyId, context.state.modifiedThemeProperties, true, response => {
        log.debug('Update theme response: ' + JSON.stringify(response));
      });
    }
  }
};

let projectListenerCounts = {};

function getProjectInfo(context, project_id) {
  return context.state.projectInfoList[project_id];
}

function getSession(context, session_id) {
  return new Promise((resolve, reject) => {
    let session = context.state.sessionList[session_id];
    if (session) {
      resolve(session);
    } else {
      sessionService.getSession(session_id, response => {
        if (response.status == 'success') {
          context.commit('sessionUpdated', response.data);
          resolve(response.data);
        } else {
          reject(response);
        }
      });
    }
  });
}

function getProject(context, tenancy_id, project_id) {
  // seems like subscribing to project does not return the actual project, only updates
  // so we need to fetch project info separately
  return new Promise((resolve, reject) => {
    let project = context.state.projectInfoList[project_id];
    if (project) {
      resolve(project);
    } else {
      projectService.getProject(tenancy_id, project_id, response => {
        if (response.status == 'success') {
          resolve(response.data);
        } else {
          reject(response);
        }
      });
    }
  });
}


//////////////////
// WEBPACK FOOTER
// ../ReactorJSCore/dist/store/projects.js
// module id = null
// module chunks = 